import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useEvent } from '../hooks/useEvents';
import LoadingSpinner from '../components/shared/LoadingSpinner';
import ErrorMessage from '../components/shared/ErrorMessage';
import AttractorScreen from '../components/kiosk/AttractorScreen';
import RecordingScreen from '../components/kiosk/RecordingScreen';
import PhotoCaptureScreen from '../components/kiosk/PhotoCaptureScreen';
import ReviewScreen from '../components/kiosk/ReviewScreen';
import SuccessScreen from '../components/kiosk/SuccessScreen';
import { IDLE_TIMEOUT } from '../utils/constants';

const SCREENS = {
  ATTRACTOR: 'attractor',
  RECORDING: 'recording',
  PHOTO_CAPTURE: 'photo_capture',
  REVIEW: 'review',
  SUCCESS: 'success'
};

function KioskPage() {
  const { eventId } = useParams();
  const { event, loading, error } = useEvent(eventId);
  const [currentScreen, setCurrentScreen] = useState(SCREENS.ATTRACTOR);
  const [capturedMedia, setCapturedMedia] = useState(null);
  const [captureMode, setCaptureMode] = useState(null);
  const [guestInfo, setGuestInfo] = useState({ name: '', email: '' });
  const [idleTimer, setIdleTimer] = useState(null);

  // Reset to attractor screen after idle timeout
  useEffect(() => {
    if (currentScreen !== SCREENS.ATTRACTOR) {
      const timer = setTimeout(() => {
        handleReset();
      }, IDLE_TIMEOUT * 1000);

      setIdleTimer(timer);

      return () => {
        if (timer) clearTimeout(timer);
      };
    }
  }, [currentScreen]);

  const handleStartCapture = (mode, name, email) => {
    setCaptureMode(mode);
    setGuestInfo({ name, email });
    setCurrentScreen(mode === 'photo' ? SCREENS.PHOTO_CAPTURE : SCREENS.RECORDING);
    if (idleTimer) clearTimeout(idleTimer);
  };

  const handleCaptureComplete = (mediaBlob) => {
    setCapturedMedia(mediaBlob);
    setCurrentScreen(SCREENS.REVIEW);
  };

  const handleRetake = () => {
    setCapturedMedia(null);
    setCurrentScreen(captureMode === 'photo' ? SCREENS.PHOTO_CAPTURE : SCREENS.RECORDING);
  };

  const handleUploadComplete = () => {
    setCurrentScreen(SCREENS.SUCCESS);
  };

  const handleReset = () => {
    setCurrentScreen(SCREENS.ATTRACTOR);
    setCapturedMedia(null);
    setCaptureMode(null);
    setGuestInfo({ name: '', email: '' });
    if (idleTimer) clearTimeout(idleTimer);
  };

  if (loading) {
    return (
      <div className="fullscreen flex items-center justify-center bg-dark-slate">
        <LoadingSpinner message="Loading event..." />
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="fullscreen flex items-center justify-center bg-dark-slate p-6">
        <ErrorMessage
          message={error || 'Event not found'}
        />
      </div>
    );
  }

  return (
    <div className="fullscreen bg-dark-slate">
      {currentScreen === SCREENS.ATTRACTOR && (
        <AttractorScreen
          event={event}
          onStart={handleStartCapture}
        />
      )}

      {currentScreen === SCREENS.RECORDING && (
        <RecordingScreen
          event={event}
          guestInfo={guestInfo}
          onComplete={handleCaptureComplete}
          onCancel={handleReset}
        />
      )}

      {currentScreen === SCREENS.PHOTO_CAPTURE && (
        <PhotoCaptureScreen
          event={event}
          guestInfo={guestInfo}
          onComplete={handleCaptureComplete}
          onCancel={handleReset}
        />
      )}

      {currentScreen === SCREENS.REVIEW && (
        <ReviewScreen
          event={event}
          mediaBlob={capturedMedia}
          mediaType={captureMode}
          guestInfo={guestInfo}
          onRetake={handleRetake}
          onUploadComplete={handleUploadComplete}
          onCancel={handleReset}
        />
      )}

      {currentScreen === SCREENS.SUCCESS && (
        <SuccessScreen
          event={event}
          onReset={handleReset}
        />
      )}
    </div>
  );
}

export default KioskPage;
